import { Link } from "react-router-dom";
import { ArrowRight, Baby, Moon, Sun, Footprints } from "lucide-react";
import { Reveal, LineReveal } from "../components/motion";
import CTABand from "../components/CTABand";
import { useSEO, usePageView } from "../lib/seo";

const AGES = [
  { icon: Baby, range: "0–4 months", title: "The newborn months", body: "Sleep is still maturing — this stage is about gentle foundations: wake windows, settling rhythms and realistic expectations, not sleep training.", link: "/sleep-help", tag: "0–6 months" },
  { icon: Moon, range: "4–12 months", title: "The big sleep shift", body: "Sleep cycles have matured, regressions hit, and habits form fast. Small, gentle routine changes now make an enormous difference.", link: "/sleep-help", tag: "4–6 months" },
  { icon: Sun, range: "1–2 years", title: "Transitions & regressions", body: "Moving to one nap, the 18-month wobble, separation anxiety — this age is all about steady, consistent responses through change.", link: "/sleep-help", tag: "7–18 months" },
  { icon: Footprints, range: "2–5 years", title: "Toddler sleep", body: "Bedtime negotiations, early wakes and big-bed moves. Toddlers need boundaries with warmth — and a routine that never changes.", link: "/sleep-help", tag: "Toddler" },
];

const PATHS = [
  { step: "1", title: "Take the 2-minute assessment", body: "Five gentle questions about your child's age, what's hardest right now, and the kind of support that feels right. You get one clear recommended path.", cta: "Take the 2-minute assessment", to: "/sleep-assessment", testid: "start-path-assessment" },
  { step: "2", title: "Book a free 15-minute suitability call", body: "Already know you want to talk? A short, no-obligation call to see whether my support is the right fit — and which option makes sense.", cta: "Book a free 15-minute suitability call", to: "/book-a-call", testid: "start-path-booking" },
  { step: "3", title: "Start with the $29 guide", body: "Gathering a plan first? The Gentle Sleep Foundations Guide gives you age-by-age frameworks to implement at your own pace, instantly.", cta: "Get the guide now", to: "/support-options#gentle-sleep-foundations-guide", testid: "start-path-guide" },
];

export default function StartHere() {
  useSEO({
    title: "Start Here — The Nanny Sleep Co.",
    description: "Overwhelmed and exhausted? Choose your child's age, find your path, and get one clear next step for better sleep — gently.",
  });
  usePageView("/start-here");

  return (
    <div data-testid="start-here-page">
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <p className="overline-label mb-5">Start here</p>
          </Reveal>
          <LineReveal
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ink"
            lines={["You don't have to read", "the whole internet tonight."]}
          />
          <Reveal delay={0.5}>
            <p className="mt-7 text-lg text-ink-soft leading-relaxed max-w-xl mx-auto">
              Take a breath. Here's everything in one place: what's normal for your child's age, and the three ways I can help — from a $29 guide to a month of close support.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-ink">First — how old is your little one?</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {AGES.map((a, i) => (
              <Reveal key={a.range} delay={i * 0.08}>
                <Link to={a.link} data-testid={`age-card-${i}`} className="card-soft p-7 block h-full group">
                  <a.icon className="w-7 h-7 text-terra mb-5" strokeWidth={1.6} />
                  <p className="overline-label mb-2">{a.range}</p>
                  <h3 className="font-serif text-xl font-medium text-ink mb-3 group-hover:text-terra transition-colors">{a.title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{a.body}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10 max-w-xl">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-ink mb-3">Then — pick the path that feels right</h2>
            <p className="text-ink-soft">There's no wrong door. Each path leads to gentle, tailored support.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {PATHS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.1}>
                <div className="card-soft p-8 h-full flex flex-col">
                  <span className="font-serif text-4xl font-light text-terra/50 mb-4">{p.step}</span>
                  <h3 className="font-serif text-xl font-medium text-ink mb-3">{p.title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed mb-7 flex-1">{p.body}</p>
                  <Link to={p.to} data-testid={p.testid} className={i === 0 ? "btn-primary w-full" : "btn-secondary w-full"}>
                    {p.cta} {i === 0 && <ArrowRight className="w-4 h-4" />}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand title="Still torn between options?" body="That's exactly what the assessment is for. Two minutes, five questions, one clear recommendation — with your privacy respected." />
    </div>
  );
}
